import { createNoise3D, type NoiseFunction3D } from "simplex-noise";
import type { GameConfig } from "../config/GameConfig";
import { bezierEaseInOut, clamp, lerp, normalize } from "../core/math";
import type { WorldBounds } from "../core/WorldBounds";
import type { ForceField } from "../physics/ForceField";

export interface DividerPoint {
  x: number;
  displayX: number;
  restOffset: number;
  phase: number;
  displayY: number;
  angle: number;
  nx: number;
  ny: number;
  radiusScale: number;
  born: number;
  thicknessBoost: number;
  wiggleBoost: number;
  pulseBoost: number;
  speedBoost: number;
  pulseSpeedBoost: number;
  absorbLift: number;
  absorbSlot?: number;
  absorbId?: number;
}

export interface SeamHit {
  x: number;
  y: number;
  angle: number;
  nx: number;
  ny: number;
  dist: number;
}

interface AbsorbProcess {
  bubbleId: number;
  contactX: number;
  contactY: number;
  contactAngle: number;
  nx: number;
  ny: number;
  side: number;
  startRadius: number;
  spheresTotal: number;
  spheresAdded: number;
  lastProgress: number;
  lastEased: number;
  paramDelta: {
    thickness: number;
    wiggleAmp: number;
    sizePulseAmp: number;
    wiggleSpeed: number;
    sizePulseSpeed: number;
  };
  bumpAmp: number;
}

export interface DividerDeposit {
  nx: number;
  ny: number;
  nr: number;
}

export class Divider {
  points: DividerPoint[] = [];
  deposits: DividerDeposit[] = [];
  bonusSpheres = 0;
  private readonly absorptions = new Map<number, AbsorbProcess>();
  private readonly noise: NoiseFunction3D;

  constructor(
    private readonly config: GameConfig,
    private readonly bounds: WorldBounds,
  ) {
    this.noise = createNoise3D();
    this.rebuild();
    this.bounds.onResize(() => this.rebuild());
  }

  get isOval(): boolean {
    return this.config.divider.shape === "oval";
  }

  get baseY(): number {
    return this.bounds.height * this.config.divider.baseY;
  }

  get cx(): number {
    return this.bounds.width * 0.5;
  }

  get cy(): number {
    return this.bounds.height * 0.5;
  }

  get rx(): number {
    return Math.max(24, this.bounds.width * (0.5 - this.config.divider.ovalInsetX));
  }

  get ry(): number {
    return Math.max(24, this.bounds.height * (0.5 - this.config.divider.ovalInsetY));
  }

  get sphereRadius(): number {
    return this.config.divider.thickness * this.config.divider.sphereRadiusFactor;
  }

  pointCount(): number {
    const spacing = Math.max(1, this.config.divider.pointSpacing);
    const path = this.isOval ? this.perimeter() : this.bounds.width;
    const base = Math.round(path / spacing) + (this.isOval ? 0 : 1);
    return Math.max(this.isOval ? 24 : 2, Math.min(2048, base + this.bonusSpheres));
  }

  reset(): void {
    this.bonusSpheres = 0;
    this.deposits = [];
    this.absorptions.clear();
    this.points = [];
    this.rebuild();
  }

  rebuild(): void {
    const count = this.pointCount();
    const old = this.points;
    this.points = [];
    if (this.isOval) {
      for (let i = 0; i < count; i++) {
        const angle = wrapAngle((i / count) * Math.PI * 2);
        const pose = this.ellipsePose(angle, 0);
        const sampled = sampleClosed(old, angle);
        this.points.push(this.makePoint(pose.x, pose.y, angle, pose.nx, pose.ny, sampled));
      }
      return;
    }
    const width = this.bounds.width;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0 : i / (count - 1);
      const x = t * width;
      const sampled = samplePolyline(old, x);
      this.points.push(
        this.makePoint(x, this.baseY + sampled.restOffset, 0, 0, 1, sampled),
      );
    }
  }

  yAt(x: number): number {
    if (this.isOval) return this.closest(x, this.cy).y;
    return interpolate(this.points, x, (p) => p.displayY, this.baseY);
  }

  restYAt(x: number): number {
    if (this.isOval) return this.closest(x, this.cy).y;
    return interpolate(this.points, x, (p) => this.baseY + p.restOffset, this.baseY);
  }

  visualAngle(x: number, y: number): number {
    return Math.atan2(y - this.cy, x - this.cx);
  }

  contains(x: number, y: number): boolean {
    return this.isAbove(x, y);
  }

  allowsSpawn(x: number, y: number): boolean {
    if (this.isOval && !this.isAbove(x, y)) return false;
    return this.distanceTo(x, y) >= this.spawnKeepOutPx();
  }

  /** Distância mínima à costura para criar/arrastar uma bolha. */
  spawnKeepOutPx(): number {
    const t = this.config.bubble.spawnKeepOut;
    if (t <= 0) return 0;
    return t * Math.min(this.bounds.width, this.bounds.height);
  }

  distanceTo(x: number, y: number): number {
    if (this.isOval) return this.closest(x, y).dist;
    return Math.abs(y - this.yAt(x));
  }

  isAbove(x: number, y: number): boolean {
    if (!this.isOval) return y < this.yAt(x);
    const hit = this.closest(x, y);
    return (x - hit.x) * hit.nx + (y - hit.y) * hit.ny < 0;
  }

  /** Fração do ecrã: `first` = cima/dentro, `second` = baixo/fora. */
  regionShares(): { first: number; second: number } {
    const total = Math.max(1, this.bounds.width * this.bounds.height);
    const first = this.isOval ? this.ovalArea() : this.areaAboveLine();
    const a = clamp(first / total, 0, 1);
    return { first: a, second: 1 - a };
  }

  closest(x: number, y: number): SeamHit {
    if (!this.isOval) {
      const ly = this.yAt(x);
      return {
        x,
        y: ly,
        angle: this.visualAngle(x, ly),
        nx: 0,
        ny: 1,
        dist: Math.abs(y - ly),
      };
    }
    let best = this.points[0];
    let bestD = Infinity;
    for (const p of this.points) {
      const d = (p.displayX - x) ** 2 + (p.displayY - y) ** 2;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return {
      x: best.displayX,
      y: best.displayY,
      angle: this.visualAngle(best.displayX, best.displayY),
      nx: best.nx,
      ny: best.ny,
      dist: Math.sqrt(bestD),
    };
  }

  pointAlong(t: number): { x: number; y: number } {
    if (!this.isOval) {
      const x = t * this.bounds.width;
      return { x, y: this.yAt(x) };
    }
    const pose = this.ellipsePose(t * Math.PI * 2, 0);
    const hit = this.closest(pose.x, pose.y);
    return { x: hit.x, y: hit.y };
  }

  update(_dt: number, time: number, field: ForceField): void {
    if (this.absorptions.size === 0 && this.points.length !== this.pointCount()) {
      this.rebuild();
    }
    this.applyLocalInfluences();
    const cfg = this.config.divider;
    const px = cfg.forceToPixels;

    for (const p of this.points) {
      const amp = cfg.wiggleAmp + p.wiggleBoost;
      const wiggleSpeed = cfg.wiggleSpeed + p.speedBoost;
      const pulse = cfg.sizePulseAmp + p.pulseBoost;
      const n = this.noise(p.x * 0.0045, time * wiggleSpeed, p.phase);
      const sample = field.sample(p.displayX, p.displayY, time, this);
      let forceOff: number;
      if (this.isOval) {
        const alongN = sample.x * p.nx + sample.y * p.ny;
        forceOff = clamp(alongN * px, -amp * 2.2, amp * 2.2);
      } else {
        forceOff = clamp(
          sample.y * px + sample.x * px * 0.35,
          -amp * 2.2,
          amp * 2.2,
        );
      }
      const radial = this.clampOvalRadial(p.restOffset + p.absorbLift + n * amp + forceOff);
      if (this.isOval) {
        const pose = this.ellipsePose(p.angle, radial);
        p.displayX = pose.x;
        p.displayY = pose.y;
        p.nx = pose.nx;
        p.ny = pose.ny;
      } else {
        p.displayX = p.x;
        p.displayY = this.baseY + radial;
      }
      const sizeN = this.noise(
        p.x * 0.011,
        time * (cfg.sizePulseSpeed + p.pulseSpeedBoost),
        p.phase + 17.3,
      );
      const born = bezierEaseInOut(p.born, cfg.absorbBezierIn, cfg.absorbBezierOut);
      const thick = 1 + p.thicknessBoost / Math.max(cfg.thickness, 1);
      p.radiusScale = (1 + sizeN * pulse) * born * thick;
    }
  }

  beginAbsorb(bubbleId: number, x: number, y: number, radius: number): void {
    if (this.absorptions.has(bubbleId)) return;

    const cfg = this.config.divider;
    const lineR = Math.max(1, this.sphereRadius);
    const ratio = radius / lineR;
    const added = Math.max(1, Math.round(ratio));
    const intensity = Math.max(1, ratio);
    const hit = this.closest(x, y);
    const side = this.isAbove(x, y) ? -1 : 1;

    this.absorptions.set(bubbleId, {
      bubbleId,
      contactX: hit.x,
      contactY: hit.y,
      contactAngle: hit.angle,
      nx: hit.nx,
      ny: hit.ny,
      side,
      startRadius: Math.max(radius, 1),
      spheresTotal: added,
      spheresAdded: 0,
      lastProgress: 0,
      lastEased: 0,
      paramDelta: this.paramDeltaFrom(intensity),
      bumpAmp: lineR * cfg.bumpStrength * Math.min(intensity, 4) * 0.35,
    });
  }

  advanceAbsorb(bubbleId: number, currentRadius: number): boolean {
    const job = this.absorptions.get(bubbleId);
    if (!job) return true;

    const progress = clamp(1 - currentRadius / job.startRadius, 0, 1);
    this.applyAbsorbProgress(job, progress);
    return progress >= 1;
  }

  finishAbsorb(bubbleId: number): void {
    const job = this.absorptions.get(bubbleId);
    if (!job) return;
    this.applyAbsorbProgress(job, 1);
    this.nudgeParams(job.paramDelta, 1);
    // A costura desloca-se para o lado que absorveu: território, não só grossura.
    const lift = job.side * job.bumpAmp;
    const floor = this.isOval ? this.ovalRadialMin() : -Infinity;
    for (const p of this.points) {
      p.restOffset = Math.max(floor, p.restOffset + lift);
    }
    this.absorptions.delete(bubbleId);
    this.applyLocalInfluences();
  }

  cancelAbsorb(bubbleId: number): void {
    const job = this.absorptions.get(bubbleId);
    if (!job) return;
    const kept = this.points.filter((p) => p.absorbId !== bubbleId);
    this.bonusSpheres -= this.points.length - kept.length;
    if (this.bonusSpheres < 0) this.bonusSpheres = 0;
    this.points = kept;
    this.absorptions.delete(bubbleId);
    this.applyLocalInfluences();
  }

  absorbEased(bubbleId: number): number {
    return this.absorptions.get(bubbleId)?.lastEased ?? 0;
  }

  absorbPose(bubbleId: number, radius: number): { x: number; y: number } | null {
    const job = this.absorptions.get(bubbleId);
    if (!job) return null;
    const progress = clamp(1 - radius / job.startRadius, 0, 1);
    const eased = this.easeAbsorb(progress);
    const pull = 1 - eased * 0.88;
    return {
      x: job.contactX + job.nx * job.side * radius * pull,
      y: job.contactY + job.ny * job.side * radius * pull,
    };
  }

  uvOval(): [number, number, number, number] {
    const w = Math.max(1, this.bounds.width);
    const h = Math.max(1, this.bounds.height);
    let ax = this.rx;
    let ay = this.ry;
    if (this.points.length > 0) {
      ax = 24;
      ay = 24;
      for (const p of this.points) {
        ax = Math.max(ax, Math.abs(p.displayX - this.cx));
        ay = Math.max(ay, Math.abs(p.displayY - this.cy));
      }
    }
    return [this.cx / w, this.cy / h, ax / w, ay / h];
  }

  private areaAboveLine(): number {
    const w = this.bounds.width;
    const pts = this.points;
    if (pts.length === 0) return w * this.baseY;
    const line: { x: number; y: number }[] = [];
    if (pts[0].displayX > 0.5) line.push({ x: 0, y: pts[0].displayY });
    for (const p of pts) line.push({ x: p.displayX, y: p.displayY });
    const last = line[line.length - 1];
    if (last.x < w - 0.5) line.push({ x: w, y: last.y });
    let area = 0;
    for (let i = 0; i < line.length - 1; i++) {
      area += (line[i + 1].x - line[i].x) * (line[i].y + line[i + 1].y) * 0.5;
    }
    return area;
  }

  private ovalArea(): number {
    const pts = this.points;
    const n = pts.length;
    if (n < 3) return Math.PI * this.rx * this.ry;
    let area = 0;
    for (let i = 0; i < n; i++) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      area += a.displayX * b.displayY - b.displayX * a.displayY;
    }
    return Math.abs(area) * 0.5;
  }

  private ovalRadialMin(): number {
    return -Math.min(this.rx, this.ry) * 0.62;
  }

  private clampOvalRadial(radial: number): number {
    if (!this.isOval) return radial;
    return clamp(radial, this.ovalRadialMin(), Math.min(this.rx, this.ry) * 0.9);
  }

  private perimeter(): number {
    const a = this.rx;
    const b = this.ry;
    return Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
  }

  private ellipsePose(
    angle: number,
    radial: number,
  ): { x: number; y: number; nx: number; ny: number } {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const n = normalize(c / this.rx, s / this.ry);
    return {
      x: this.cx + this.rx * c + n.x * radial,
      y: this.cy + this.ry * s + n.y * radial,
      nx: n.x,
      ny: n.y,
    };
  }

  private makePoint(
    x: number,
    y: number,
    angle: number,
    nx: number,
    ny: number,
    sampled: { restOffset: number; phase: number },
  ): DividerPoint {
    return {
      x,
      displayX: x,
      restOffset: sampled.restOffset,
      phase: sampled.phase,
      displayY: y,
      angle: wrapAngle(angle),
      nx,
      ny,
      radiusScale: 1,
      born: 1,
      thicknessBoost: 0,
      wiggleBoost: 0,
      pulseBoost: 0,
      speedBoost: 0,
      pulseSpeedBoost: 0,
      absorbLift: 0,
    };
  }

  private easeAbsorb(progress: number): number {
    if (progress <= 0) return 0;
    if (progress >= 1) return 1;
    const cfg = this.config.divider;
    return bezierEaseInOut(progress, cfg.absorbBezierIn, cfg.absorbBezierOut);
  }

  private applyAbsorbProgress(job: AbsorbProcess, progress: number): void {
    const eased = this.easeAbsorb(progress);
    const dE = eased - job.lastEased;
    if (dE <= 0 && progress < 1) return;

    if (dE > 0) {
      job.lastEased = eased;
      job.lastProgress = progress;
    }

    const shouldHave =
      eased >= 1
        ? job.spheresTotal
        : Math.min(
            job.spheresTotal,
            Math.max(eased > 0 ? 1 : 0, Math.ceil(eased * job.spheresTotal)),
          );

    while (job.spheresAdded < shouldHave) {
      this.insertOneSphere(job);
    }

    const exact = eased * job.spheresTotal;
    for (const p of this.points) {
      if (p.absorbSlot === undefined || p.absorbId !== job.bubbleId) continue;
      p.born = clamp(exact - p.absorbSlot, 0, 1);
    }
  }

  private paramDeltaFrom(intensity: number): AbsorbProcess["paramDelta"] {
    const cfg = this.config.divider;
    const grow = (current: number, rate: number, max: number) =>
      Math.min(max, current * (1 + rate * intensity)) - current;
    return {
      thickness: grow(cfg.thickness, cfg.absorbSizeGrow, cfg.thicknessMax),
      wiggleAmp: grow(cfg.wiggleAmp, cfg.absorbWiggleGrow, cfg.wiggleAmpMax),
      sizePulseAmp: grow(cfg.sizePulseAmp, cfg.absorbPulseGrow, cfg.sizePulseAmpMax),
      wiggleSpeed: grow(cfg.wiggleSpeed, cfg.absorbSpeedGrow, cfg.wiggleSpeedMax),
      sizePulseSpeed: grow(cfg.sizePulseSpeed, cfg.absorbSpeedGrow * 0.7, cfg.sizePulseSpeedMax),
    };
  }

  private nudgeParams(delta: AbsorbProcess["paramDelta"], dP: number): void {
    const cfg = this.config.divider;
    cfg.thickness = Math.min(cfg.thicknessMax, cfg.thickness + delta.thickness * dP);
    cfg.wiggleAmp = Math.min(cfg.wiggleAmpMax, cfg.wiggleAmp + delta.wiggleAmp * dP);
    cfg.sizePulseAmp = Math.min(cfg.sizePulseAmpMax, cfg.sizePulseAmp + delta.sizePulseAmp * dP);
    cfg.wiggleSpeed = Math.min(cfg.wiggleSpeedMax, cfg.wiggleSpeed + delta.wiggleSpeed * dP);
    cfg.sizePulseSpeed = Math.min(cfg.sizePulseSpeedMax, cfg.sizePulseSpeed + delta.sizePulseSpeed * dP);
  }

  private spatialWeight(point: DividerPoint, job: AbsorbProcess, progress: number): number {
    const cfg = this.config.divider;
    let dist: number;
    if (this.isOval) {
      dist = Math.abs(wrapAngle(point.angle - job.contactAngle)) / Math.PI;
    } else {
      dist = Math.abs(point.x - job.contactX) / Math.max(1, this.bounds.width);
    }
    const spreadT = bezierEaseInOut(progress, cfg.absorbBezierIn, cfg.absorbBezierOut);
    const flatten = bezierEaseInOut(progress, cfg.absorbBalanceIn, cfg.absorbBalanceOut);
    const spread = lerp(cfg.absorbSpreadStart, 1.2, spreadT);
    const u = clamp(dist / Math.max(0.001, spread), 0, 1);
    const local = 1 - bezierEaseInOut(u, cfg.absorbFalloffIn, cfg.absorbFalloffOut);
    return lerp(local, 1, flatten);
  }

  private applyLocalInfluences(): void {
    for (const p of this.points) {
      p.thicknessBoost = 0;
      p.wiggleBoost = 0;
      p.pulseBoost = 0;
      p.speedBoost = 0;
      p.pulseSpeedBoost = 0;
      p.absorbLift = 0;
    }
    for (const job of this.absorptions.values()) {
      const eased = job.lastEased;
      if (eased <= 0 && job.lastProgress <= 0) continue;
      for (const p of this.points) {
        const w = this.spatialWeight(p, job, job.lastProgress);
        const k = eased * w;
        p.thicknessBoost += job.paramDelta.thickness * k;
        p.wiggleBoost += job.paramDelta.wiggleAmp * k;
        p.pulseBoost += job.paramDelta.sizePulseAmp * k;
        p.speedBoost += job.paramDelta.wiggleSpeed * k;
        p.pulseSpeedBoost += job.paramDelta.sizePulseSpeed * k;
        p.absorbLift += job.side * job.bumpAmp * k;
      }
    }
  }

  private insertOneSphere(job: AbsorbProcess): void {
    const k = job.spheresAdded;
    const spacing = Math.max(1.2, this.config.divider.pointSpacing * 0.7);
    if (this.isOval) {
      const dAng =
        k === 0 ? 0 : Math.ceil(k / 2) * (spacing / Math.max(1, this.perimeter())) * Math.PI * 2 * (k % 2 === 1 ? -1 : 1);
      const angle = wrapAngle(job.contactAngle + dAng);
      const sampled = sampleClosed(this.points, angle);
      const rest = this.clampOvalRadial(sampled.restOffset + job.side * this.sphereRadius * 0.18);
      const pose = this.ellipsePose(angle, rest);
      this.points.push(
        this.makePoint(pose.x, pose.y, angle, pose.nx, pose.ny, {
          restOffset: rest,
          phase: sampled.phase,
        }),
      );
      const added = this.points[this.points.length - 1];
      added.radiusScale = 0;
      added.born = 0;
      added.absorbSlot = k;
      added.absorbId = job.bubbleId;
      this.points.sort((a, b) => wrapAngle(a.angle) - wrapAngle(b.angle));
    } else {
      const offset =
        k === 0 ? 0 : Math.ceil(k / 2) * spacing * (k % 2 === 1 ? -1 : 1);
      const px = clamp(job.contactX + offset, 0, this.bounds.width);
      const rest = interpolate(this.points, px, (p) => p.restOffset, 0);
      const inflate = this.sphereRadius * 0.18;
      this.points.push(
        this.makePoint(px, this.baseY + rest + job.side * inflate, 0, 0, 1, {
          restOffset: rest + job.side * inflate,
          phase: px * 0.041 + Math.random() * 8,
        }),
      );
      const added = this.points[this.points.length - 1];
      added.radiusScale = 0;
      added.born = 0;
      added.absorbSlot = k;
      added.absorbId = job.bubbleId;
      this.points.sort((a, b) => a.x - b.x);
    }
    this.bonusSpheres += 1;
    job.spheresAdded += 1;
  }

  writeDividerYMap(image: ImageData): void {
    const data = image.data;
    const width = image.width;
    for (let i = 0; i < width; i++) {
      const x = (i / Math.max(1, width - 1)) * this.bounds.width;
      const yNorm = clamp(this.yAt(x) / this.bounds.height, 0, 1);
      const p = i * 4;
      data[p] = yNorm * 255;
      data[p + 1] = 0;
      data[p + 2] = 0;
      data[p + 3] = 255;
    }
  }
}

function wrapAngle(a: number): number {
  let x = (a + Math.PI) % (Math.PI * 2);
  if (x < 0) x += Math.PI * 2;
  return x - Math.PI;
}

function interpolate(
  points: DividerPoint[],
  x: number,
  pick: (p: DividerPoint) => number,
  fallback: number,
): number {
  const count = points.length;
  if (count === 0) return fallback;
  if (count === 1) return pick(points[0]);
  if (x <= points[0].x) return pick(points[0]);
  if (x >= points[count - 1].x) return pick(points[count - 1]);

  let lo = 0;
  let hi = count - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (points[mid].x <= x) lo = mid;
    else hi = mid;
  }
  const span = points[hi].x - points[lo].x;
  const f = span < 1e-6 ? 0 : (x - points[lo].x) / span;
  return pick(points[lo]) * (1 - f) + pick(points[hi]) * f;
}

function samplePolyline(
  old: DividerPoint[],
  x: number,
): { restOffset: number; phase: number } {
  if (old.length === 0) {
    return { restOffset: 0, phase: x * 0.041 };
  }
  return {
    restOffset: interpolate(old, x, (p) => p.restOffset, 0),
    phase: interpolate(old, x, (p) => p.phase, x * 0.041),
  };
}

function sampleClosed(
  old: DividerPoint[],
  angle: number,
): { restOffset: number; phase: number } {
  if (old.length === 0) {
    return { restOffset: 0, phase: angle * 3.1 };
  }
  if (old.length === 1) {
    return { restOffset: old[0].restOffset, phase: old[0].phase };
  }
  const a = wrapAngle(angle);
  const n = old.length;
  let lo = 0;
  let t = 0;
  let found = false;
  for (let i = 0; i < n; i++) {
    const a0 = wrapAngle(old[i].angle);
    const a1 = wrapAngle(old[(i + 1) % n].angle);
    let span = a1 - a0;
    if (span <= 0) span += Math.PI * 2;
    let u = a - a0;
    if (u < 0) u += Math.PI * 2;
    if (u <= span + 1e-6) {
      lo = i;
      t = span < 1e-6 ? 0 : u / span;
      found = true;
      break;
    }
  }
  if (!found) {
    let best = old[0];
    let bestD = Infinity;
    for (const p of old) {
      const d = Math.abs(wrapAngle(p.angle - a));
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return { restOffset: best.restOffset, phase: best.phase };
  }
  const p0 = old[lo];
  const p1 = old[(lo + 1) % n];
  const f = clamp(t, 0, 1);
  return {
    restOffset: p0.restOffset * (1 - f) + p1.restOffset * f,
    phase: p0.phase * (1 - f) + p1.phase * f,
  };
}
